import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { Box, IconButton, Paper, Typography } from '@mui/material'
import React, { useRef, useState } from 'react'

/**
 * Video - A reusable video player component
 *
 * @param {Object} props
 * @param {string} props.src - Video source URL
 * @param {string} [props.poster] - Poster image URL (thumbnail before play)
 * @param {string} [props.alt='Video'] - Alt text for the video
 * @param {boolean} [props.controls=true] - Whether to show video controls
 * @param {boolean} [props.autoplay=false] - Whether to autoplay the video
 * @param {boolean} [props.loop=false] - Whether to loop the video
 * @param {boolean} [props.muted=false] - Whether the video is muted
 * @param {string} [props.title] - Video title
 * @param {Object} [props.sx] - Additional Material-UI sx styles
 */
const Video = ({ src, poster, alt = 'Video', controls = true, autoplay = false, loop = false, muted = false, title, sx = {} }) => {
  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showPlayButton, setShowPlayButton] = useState(!autoplay)

  // Handle play/pause
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
        setIsPlaying(false)
        setShowPlayButton(true)
      } else {
        videoRef.current.play()
        setIsPlaying(true)
        setShowPlayButton(false)
      }
    }
  }

  // Handle video events
  const handlePlay = () => {
    setIsPlaying(true)
    setShowPlayButton(false)
  }

  const handlePause = () => {
    setIsPlaying(false)
    setShowPlayButton(true)
  }

  const handleEnded = () => {
    setIsPlaying(false)
    setShowPlayButton(true)
  }

  if (!src) {
    return null
  }

  return (
    <Paper
      elevation={2}
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        borderRadius: 2,
        ...sx,
      }}
    >
      {title && (
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" component="h3">
            {title}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          backgroundColor: 'black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: controls ? 'pointer' : 'default',
        }}
        onClick={!controls ? handlePlayPause : undefined}
      >
        <Box
          component="video"
          ref={videoRef}
          src={src}
          poster={poster}
          controls={controls}
          autoPlay={autoplay}
          loop={loop}
          muted={muted}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            maxHeight: '80vh',
          }}
        >
          Your browser does not support the video tag.
        </Box>

        {/* Custom play button overlay (shown when controls are disabled or video is paused) */}
        {!controls && showPlayButton && (
          <IconButton
            onClick={handlePlayPause}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              width: 64,
              height: 64,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                transform: 'translate(-50%, -50%) scale(1.1)',
              },
              transition: 'all 0.3s',
            }}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            <PlayArrowIcon sx={{ fontSize: 40 }} />
          </IconButton>
        )}
      </Box>
    </Paper>
  )
}

export default Video
